import React, { useEffect, useState } from "react";
import { Gift, Wallet, Sparkles, ImageIcon, Video, Loader2, CheckCircle2, BadgePercent, Plus, Coins, Info, BookOpen, ArrowDown, FileText, ShoppingCart } from "lucide-react";
import { Link } from "react-router-dom";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { mediaUrl } from "@/lib/download";
import { Watermarked } from "@/components/Watermarked";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import api, { formatError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { LiabilityConsentDialog } from "@/components/LiabilityConsent";
import { PaymentSoonDialog } from "@/components/PaymentSoonDialog";

const SIZES = {
  image: ["1:1", "4:5", "9:16", "16:9"], logo: ["1:1"], business_card: ["16:9"], cover: ["16:9"],
  menu: ["9:16", "4:5"], infographic: ["9:16", "4:5"], brochure: ["16:9"], video: ["9:16", "1:1", "16:9"],
};
const PRINTABLE = new Set(["business_card", "cover", "menu", "infographic", "brochure", "logo"]);  // كل قطع التصميم لها PDF طباعة A4 بما يناسبها
export const kindSizes = (k) => SIZES[k] || SIZES.image;
export const kindPrintable = (k) => PRINTABLE.has(k);

/** دليل الأنواع: شرح كل قطعة، كيفية استخدامها، المدخلات المطلوبة، وعينات مما سيُولَّد */
/** معاينة عينة بالحجم الكامل */
export const SampleLightbox = ({ src, onClose }) => (
  <Dialog open={!!src} onOpenChange={(o) => !o && onClose()}>
    <DialogContent className="max-w-3xl p-2" data-testid="sample-lightbox">
      <DialogHeader><DialogTitle className="text-sm">معاينة العينة — محمية بعلامة رواج الذكية المائية</DialogTitle></DialogHeader>
      {src && <Watermarked className="rounded-lg"><img src={src} alt="" draggable={false} className="w-full max-h-[75vh] object-contain rounded-lg" /></Watermarked>}
    </DialogContent>
  </Dialog>
);

/** عينة مجانية مشابهة (منخفضة الدقة، لا تُخصم) مع توضيح أن النتيجة النهائية بجودة العينات المرفقة */
export const FreeSample = ({ kind, prompt, size, mood, brandName }) => {
  const [busy, setBusy] = useState(false);
  const [res, setRes] = useState(null);
  const [text, setText] = useState(prompt || "");
  useEffect(() => { if (prompt) setText(prompt); }, [prompt]);
  const run = async () => {
    if (!text.trim()) return toast.error("اكتب وصفًا قصيرًا لما تريده");
    setBusy(true);
    try {
      const { data } = await api.post("/content/preview-image", { prompt: text, item_kind: kind, size: size || "", mood: mood || "", brand_name: brandName || "" }, { timeout: 200000 });
      setRes(data);
    } catch (e) { toast.error(formatError(e.response?.data?.detail) || "تعذّر توليد العينة"); } finally { setBusy(false); }
  };
  return (
    <div className="rounded-xl border border-dashed border-primary/40 bg-primary/5 p-3 space-y-2" data-testid="free-sample-box">
      <div className="text-xs font-bold flex items-center gap-1"><Sparkles className="w-3.5 h-3.5 text-primary" /> ولّد عينة مجانية مشابهة لفكرتك (مصغّرة، لا تُخصم من رصيدك)</div>
      <div className="flex gap-2">
        <Input value={text} onChange={(e) => setText(e.target.value)} placeholder="صف باختصار ما تريده…" className="bg-background h-9 text-xs" data-testid="free-sample-input" />
        <Button size="sm" onClick={run} disabled={busy} className="gap-1 shrink-0" data-testid="free-sample-btn">{busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Sparkles className="w-4 h-4" />} عينة مجانية</Button>
      </div>
      {busy && <p className="text-[11px] text-muted-foreground">تُجهَّز العينة خلال نحو دقيقة…</p>}
      {res && (
        <div className="grid grid-cols-[160px_1fr] gap-3 items-start" data-testid="free-sample-result">
          <Watermarked className="rounded-lg" label={false}><img src={res.image} alt="" draggable={false} className="rounded-lg border border-border w-full" /></Watermarked>
          <div className="text-xs space-y-1">
            <p className="font-bold text-amber-800 dark:text-amber-300">⚠ {res.note}</p>
            <p className="text-muted-foreground">المتبقي من العينات المجانية اليوم: {res.left_today}</p>
          </div>
        </div>
      )}
    </div>
  );
};

export const GuideDialog = ({ guide, kind, onClose, onPick }) => {
  const [cur, setCur] = useState(kind || "image");
  const [big, setBig] = useState("");
  useEffect(() => { if (kind) setCur(kind); }, [kind]);
  const it = (guide || []).find((g) => g.kind === cur);
  return (
    <Dialog open={!!kind} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-2xl max-h-[88vh] overflow-y-auto" data-testid="guide-dialog">
        <DialogHeader><DialogTitle className="flex items-center gap-2"><BookOpen className="w-5 h-5 text-primary" /> دليل أنواع التوليد</DialogTitle></DialogHeader>
        <div className="flex flex-wrap gap-1.5">
          {(guide || []).map((g) => (
            <button key={g.kind} type="button" onClick={() => setCur(g.kind)} data-testid={`guide-tab-${g.kind}`}
              className={`rounded-full border px-3 py-1 text-xs ${cur === g.kind ? "bg-primary text-white border-primary" : "bg-secondary/60 border-border"}`}>{g.label}</button>
          ))}
        </div>
        {it && (
          <div className="space-y-3 text-sm" data-testid={`guide-body-${it.kind}`}>
            <p className="leading-relaxed"><b>ما هو:</b> {it.what}</p>
            <div>
              <b className="block mb-1">كيف تستخدمه:</b>
              <ol className="space-y-1 text-xs">{(it.how || []).map((h, i) => <li key={i} className="flex gap-2"><span className="inline-flex w-5 h-5 rounded-full bg-primary/15 text-primary items-center justify-center text-[11px] font-bold shrink-0">{i + 1}</span>{h}</li>)}</ol>
            </div>
            <p className="text-xs text-muted-foreground"><b className="text-foreground">المدخلات المطلوبة:</b> {it.inputs}</p>
            {it.scope?.length > 0 && (
              <div className="rounded-lg border border-primary/30 bg-primary/5 px-3 py-2" data-testid={`guide-scope-${it.kind}`}>
                <b className="block text-xs mb-1">نطاق الخدمة — السعر {it.price ? `${it.price} ريال` : "مجانًا"} ويشمل:</b>
                <ul className="space-y-0.5 text-xs text-muted-foreground">{it.scope.map((x, i) => <li key={i} className="flex gap-1.5"><CheckCircle2 className="w-3.5 h-3.5 text-primary shrink-0 mt-0.5" />{x}</li>)}</ul>
              </div>
            )}
            {it.digital_card && (
              <div className="rounded-lg border border-dashed border-accent/50 bg-accent/5 px-3 py-2 text-xs" data-testid="guide-digital-card">
                <b className="block mb-0.5">{it.digital_card.label} — <span className="text-emerald-700 dark:text-emerald-400">{it.digital_card.price ? `${it.digital_card.price} ريال` : "مجانًا حاليًا"}</span> (خدمة مستقلة عن تصميم البطاقة)</b>
                <p className="text-muted-foreground leading-relaxed">{it.digital_card.note}</p>
              </div>
            )}
            {it.group === "text" ? (
              <p className="text-xs rounded-lg border border-emerald-500/40 bg-emerald-500/5 px-3 py-2" data-testid="guide-text-note">مخرجات نصية تظهر مباشرة في الصفحة ويمكن نسخها وتعديلها — <b>مجانية بلا حد</b>.</p>
            ) : (
            <div>
              <b className="block mb-1">عينات مما سيُولَّد:</b>
              {it.samples?.length ? (
                <div className="grid grid-cols-3 gap-2">
                  {it.samples.map((sm) => it.group === "video"
                    ? <Watermarked key={sm.id} className="rounded-lg"><video src={mediaUrl(sm.url)} className="rounded-lg w-full aspect-[9/16] object-cover bg-black" muted controls controlsList="nodownload" disablePictureInPicture data-testid={`guide-sample-${sm.id}`} /></Watermarked>
                    : <Watermarked key={sm.id} className="rounded-lg" label={false}><button type="button" onClick={() => setBig(mediaUrl(sm.url))} className="block w-full" data-testid={`guide-sample-${sm.id}`}><img src={mediaUrl(sm.url)} alt={sm.title} draggable={false} className="rounded-lg w-full object-cover border border-border hover:opacity-90" /></button></Watermarked>)}
                </div>
              ) : <p className="text-xs text-muted-foreground">ستُضاف عينات لهذا النوع قريبًا.</p>}
              <p className="text-[11px] text-muted-foreground mt-1">اضغط العينة لمعاينتها بالحجم الكامل — هذه هي جودة النتيجة النهائية التي ستحصل عليها. العينات محمية بعلامة رواج الذكية المائية.</p>
            </div>
            )}
            {it.group === "image" && <FreeSample kind={it.kind} />}
            <Button className="w-full gap-2" onClick={() => { onPick(it); onClose(); }} data-testid="guide-pick-btn"><Sparkles className="w-4 h-4" /> اختر «{it.label}» وانتقل للحقول</Button>
          </div>
        )}
      </DialogContent>
      <SampleLightbox src={big} onClose={() => setBig("")} />
    </Dialog>
  );
};

const KIND_ICON = { video: Video, text: FileText };

const creditLabel = (it, credits, unlimited) => {
  if (!it.price) return "مجاني";
  if (unlimited) return "غير محدود";
  const n = credits[it.kind] || 0;
  if (n) return `رصيدك ×${n}`;
  const fb = it.fallback || (it.group === "image" && it.kind !== "image" ? "image" : "");
  const g = fb ? credits[fb] || 0 : 0;
  return g ? `من رصيد ${fb === "video" ? "الفيديو" : "الصور"} ×${g}` : "لا رصيد";
};

/** اختيار نوع التوليد من قائمة منسدلة واحدة (بلا زحمة): كل خيار مع رصيده، وزر ⓘ يشرح النوع المختار مع عينات */
const TypeChooser = ({ catalog, credits, selected, onPick, onInfo, unlimited }) => {
  const total = Object.values(credits || {}).reduce((a, b) => a + b, 0);
  const sel = catalog.find((c) => c.kind === selected);
  const groups = [["image", "تصاميم وصور"], ["video", "فيديو"], ["text", "نصوص مجانية"]];
  return (
    <div data-testid="credits-counter">
      {unlimited && <p className="text-xs rounded-lg border border-accent/40 bg-accent/10 px-3 py-1.5 mb-2" data-testid="admin-unlimited-note">وضع الأدمن: كل أنواع التوليد الفردي والخدمات المدفوعة مفعّلة بلا حدود للاختبار.</p>}
      <div className="text-sm font-bold mb-1 flex items-center gap-1"><Coins className="w-4 h-4 text-accent" /> ١ · اختر نوع التوليد {total && !unlimited ? <span className="text-muted-foreground font-normal text-xs">(رصيدك {total} خدمة)</span> : null}</div>
      {total === 0 && !unlimited && <p className="text-xs text-amber-800 dark:text-amber-300 rounded-lg border border-dashed border-amber-500/50 bg-amber-500/5 px-3 py-2 mb-2" data-testid="credits-empty">لا يوجد رصيد خدمات بعد — اشترِ خدمات من «الباقات والأرصدة» أو فعّل كوبون الهدية بالأسفل. النصوص متاحة مجانًا.</p>}
      <div className="flex items-center gap-2">
        <Select value={selected || ""} onValueChange={(k) => { const it = catalog.find((c) => c.kind === k); if (it) onPick(it); }}>
          <SelectTrigger className="flex-1 bg-background border-border h-11" data-testid="type-select"><SelectValue placeholder="اختر نوع التوليد…" /></SelectTrigger>
          <SelectContent className="max-h-80">
            {groups.map(([g, title]) => {
              const its = catalog.filter((c) => c.group === g);
              if (!its.length) return null;
              const Icon = KIND_ICON[g] || ImageIcon;
              return (
                <div key={g}>
                  <div className="px-2 pt-2 pb-1 text-[11px] text-muted-foreground flex items-center gap-1"><Icon className="w-3 h-3" /> {title}</div>
                  {its.map((it) => (
                    <SelectItem key={it.kind} value={it.kind} data-testid={`type-option-${it.kind}`}>
                      <span className="flex items-center gap-2">{it.label}{it.flow && <span className="text-[9px] rounded-full bg-primary/10 text-primary px-1.5 py-px">مركّب</span>}
                        <span className="text-[11px] text-emerald-700 dark:text-emerald-400">· {creditLabel(it, credits, unlimited)}</span></span>
                    </SelectItem>
                  ))}
                </div>
              );
            })}
          </SelectContent>
        </Select>
        {sel && <button type="button" onClick={() => onInfo(sel.kind)} className="shrink-0 h-11 px-3 rounded-xl border border-border bg-background flex items-center gap-1 text-xs text-primary hover:bg-primary/10" data-testid={`type-info-${sel.kind}`}><Info className="w-4 h-4" /> شرح وعينات</button>}
      </div>
      {sel && (
        <div className="mt-2 rounded-xl border border-primary bg-primary/10 p-2 text-xs flex items-center justify-between gap-2" data-testid={`type-card-${sel.kind}`}>
          <span className="font-bold">{sel.label}</span>
          <b className="text-emerald-700 dark:text-emerald-400" data-testid={`credit-count-${sel.kind}`}>{creditLabel(sel, credits, unlimited)}</b>
        </div>
      )}
      <p className="text-[11px] text-muted-foreground mt-1">بعد الاختيار تنتقل مباشرة إلى حقول الخدمة بالأسفل (مظللة بالبرتقالي). نص المنشور وسكربت الفيديو مجانيان.</p>
    </div>
  );
};

/** إضافة قطع: قائمة منسدلة (المدفوعة فقط — النصوص مجانية) + العدد → إن كفى الرصيد تُضاف فورًا، وإلا زر «ادفع» */
export const AddItems = ({ wallet, selected, onPick, onChanged, onPay }) => {
  const catalog = (wallet.catalog || []).filter((c) => c.price > 0);
  const [kind, setKind] = useState(selected || "image");
  const [qty, setQty] = useState(1);
  const [busy, setBusy] = useState(false);
  useEffect(() => { if (selected && catalog.some((c) => c.kind === selected)) setKind(selected); }, [selected]); // eslint-disable-line react-hooks/exhaustive-deps
  const it = catalog.find((c) => c.kind === kind) || catalog[0];
  const total = (Number(qty) || 0) * (it?.price || 0);
  const enough = wallet.balance >= total && total > 0;
  const qtyRef = React.useRef(null);
  const pickKind = (k) => { setKind(k); setTimeout(() => qtyRef.current?.focus(), 50); };  // اختيار القطعة هنا = تحديد العدد ثم الدفع/الإضافة (لا انتقال للحقول)
  const add = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/credits/buy", { items: { [kind]: Number(qty) } });
      toast.success(`أُضيفت ${qty} × ${it.label} لرصيدك — المتبقي بالمحفظة ${data.balance} ريال`);
      window.dispatchEvent(new Event("rawaj:usage-changed"));
      onChanged?.();
    } catch (e) { toast.error(formatError(e.response?.data?.detail)); } finally { setBusy(false); }
  };
  return (
    <div className="rounded-xl border border-border bg-secondary/30 p-3" data-testid="add-items-box">
      <div className="text-sm font-bold mb-2 flex items-center gap-1"><Plus className="w-4 h-4 text-primary" /> اشترِ خدمات وأضفها إلى رصيدك <span className="text-xs text-muted-foreground font-normal">(إن لم يكن لديك رصيد من الخدمة المختارة)</span></div>
      <div className="grid grid-cols-[1fr_auto] md:grid-cols-[1fr_90px_auto] gap-2 items-end">
        <div>
          <label className="text-[11px] text-muted-foreground block mb-1">الخدمة</label>
          <Select value={kind} onValueChange={pickKind}>
            <SelectTrigger className="bg-background border-border h-9" data-testid="add-items-kind"><SelectValue /></SelectTrigger>
            <SelectContent className="bg-popover border-border">
              {catalog.map((c) => <SelectItem key={c.kind} value={c.kind} data-testid={`add-items-kind-${c.kind}`}>{c.label}</SelectItem>)}
            </SelectContent>
          </Select>
          {it && <div className="text-[11px] text-primary mt-1" data-testid="add-items-price">سعر الخدمة: <b>{it.price} ريال</b> للوحدة</div>}
        </div>
        <div>
          <label className="text-[11px] text-muted-foreground block mb-1">الكمية</label>
          <Input ref={qtyRef} type="number" min={1} max={200} value={qty} onChange={(e) => setQty(e.target.value)} className="bg-background border-border h-9 w-20 md:w-full" data-testid="add-items-qty" />
        </div>
        <div className="col-span-2 md:col-span-1">
          {enough ? (
            <Button onClick={add} disabled={busy} className="h-9 w-full bg-primary text-white gap-1" data-testid="add-items-add-btn">
              {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />} أضف للرصيد · {total} ريال
            </Button>
          ) : (
            <Button onClick={() => onPay(total)} disabled={total <= 0} className="h-9 w-full bg-accent hover:bg-accent/90 text-accent-foreground gap-1" data-testid="add-items-pay-btn">
              <Wallet className="w-4 h-4" /> ادفع {total} ريال
            </Button>
          )}
        </div>
      </div>
      <p className="text-[11px] text-muted-foreground mt-2" data-testid="add-items-hint">
        {enough ? `يُخصم ${total} ريال من محفظتك (${wallet.balance} ريال) وتصبح الخدمات جاهزة للتوليد فورًا.` : total > 0 ? `محفظتك ${wallet.balance} ريال لا تكفي — المبلغ المستحق ${total} ريال${wallet.tax_note && /15%/.test(wallet.tax_note) ? " + ضريبة القيمة المضافة 15% عند الدفع" : " (بلا ضريبة قيمة مضافة)"}.` : "اختر الخدمة والكمية."}
      </p>
    </div>
  );
};

/** كوبون الهدية داخل التوليد الفردي مع خطوات ١-٢-٣ واضحة */
const GiftSteps = ({ wallet, onActivated }) => {
  const { setUser } = useAuth();
  const [code, setCode] = useState("");
  const [busy, setBusy] = useState(false);
  const [consent, setConsent] = useState(false);
  const activate = async () => {
    setBusy(true);
    try {
      await api.post("/coupons/validate", { code: code.trim() });
      await api.post("/subscribe", { plan: "trial", coupon: code.trim(), accepted_liability: true });
      const me = await api.get("/auth/me");
      setUser(me.data);
      toast.success(`تم تفعيل الهدية 🎁 صورتان + فيديو + ${wallet.gift_wallet_sar} ريال رصيد`);
      window.dispatchEvent(new Event("rawaj:usage-changed"));
      onActivated?.();
    } catch (e) { toast.error(formatError(e.response?.data?.detail) || "الكوبون غير صالح"); } finally { setBusy(false); setConsent(false); }
  };
  const steps = [
    ["أدخل كوبون الهدية", "الكود الذي وصلك من رواج الذكية أو من الصفحة الرئيسية"],
    ["تحصل فورًا على الهدية", `صورتان + فيديو في رصيد الخدمات (صالحة 30 يومًا) + ${wallet.gift_wallet_sar} ريال محفظة تشتري بها أي خدمة`],
    ["اختر الخدمة وولّد", "اختر الخدمة من رصيدك بالأعلى، أكمل الحقول بالأسفل ثم اضغط توليد — تُخصم خدمة واحدة عند البدء وتُعاد إذا فشل التوليد"],
  ];
  return (
    <div className="rounded-2xl border border-accent/40 bg-accent/5 p-4" data-testid="gift-coupon-box">
      <div className="flex items-center gap-2 mb-3"><Gift className="w-5 h-5 text-accent" /><h3 className="font-bold">كوبون الهدية — التوليد المجاني في ٣ خطوات</h3></div>
      <ol className="grid md:grid-cols-3 gap-2 mb-3">
        {steps.map(([t, d], i) => (
          <li key={i} className="rounded-xl bg-background/70 border border-border p-3" data-testid={`gift-step-${i + 1}`}>
            <div className="flex items-center gap-2 text-sm font-bold"><span className="w-6 h-6 rounded-full bg-accent/15 text-accent flex items-center justify-center text-xs">{["١", "٢", "٣"][i]}</span>{t}</div>
            <p className="text-xs text-muted-foreground mt-1 leading-relaxed">{d}</p>
          </li>
        ))}
      </ol>
      <div className="flex gap-2 flex-wrap">
        <Input value={code} onChange={(e) => setCode(e.target.value.toUpperCase())} placeholder="كود الكوبون" dir="ltr"
          className="bg-background border-border h-10 flex-1 min-w-[180px] text-start" data-testid="gift-coupon-input" />
        <Button onClick={() => setConsent(true)} disabled={busy || !code.trim()} className="bg-accent hover:bg-accent/90 text-accent-foreground gap-1 h-10" data-testid="gift-coupon-activate-btn">
          {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <BadgePercent className="w-4 h-4" />} فعّل الهدية
        </Button>
      </div>
      <LiabilityConsentDialog open={consent} onClose={() => setConsent(false)} onAccept={activate} title="إقرار قبل تفعيل الهدية" />
    </div>
  );
};

/** رأس التوليد الفردي: عدّاد الرصيد → إضافة قطع (قائمة + عدد → أضف/ادفع) → كوبون الهدية */
export const IndividualHub = ({ selected, selectedMode, onPick }) => {
  const { user, setUser } = useAuth();
  const [wallet, setWallet] = useState(null);
  const [pay, setPay] = useState(null);
  const [promo, setPromo] = useState(null);
  const [busy, setBusy] = useState(false);
  const [guide, setGuide] = useState([]);
  const [info, setInfo] = useState("");
  const navigate = useNavigate();
  const load = () => api.get("/wallet").then((r) => setWallet(r.data)).catch(() => {});
  useEffect(() => { load(); api.get("/coupons/promo").then((r) => setPromo(r.data)).catch(() => {}); api.get("/catalog/guide").then((r) => setGuide(r.data)).catch(() => {}); const h = () => load(); window.addEventListener("rawaj:usage-changed", h); return () => window.removeEventListener("rawaj:usage-changed", h); }, []);
  const activateFromPay = async (code) => {
    setBusy(true);
    try {
      await api.post("/subscribe", { plan: "trial", coupon: code, accepted_liability: true });
      const me = await api.get("/auth/me"); setUser(me.data);
      toast.success("تم تفعيل الهدية 🎁"); setPay(null); load();
      window.dispatchEvent(new Event("rawaj:usage-changed"));
    } catch (e) { toast.error(formatError(e.response?.data?.detail)); } finally { setBusy(false); }
  };
  if (!wallet) return null;
  const sel = (wallet.catalog || []).find((c) => c.kind === selected);
  return (
    <div className="space-y-4" data-testid="individual-hub">
      <div className="glass rounded-2xl p-5 border border-primary/30 space-y-4">
        <div className="flex items-start justify-between gap-3 flex-wrap">
          <div>
            <h2 className="font-extrabold text-lg flex items-center gap-2"><Sparkles className="w-5 h-5 text-primary" /> خدمات حسب الطلب — بدون اشتراك</h2>
            <p className="text-xs text-muted-foreground mt-1">اختر الخدمة والكمية، وادفع حسب احتياجك دون اشتراك شهري. تُضاف الخدمات المشتراة إلى رصيدك لاستخدامها لاحقًا — وتعاين الشكل المتوقع مجانًا قبل التوليد.</p>
          </div>
          <Button size="sm" variant="outline" className="gap-1.5 border-primary/40 text-primary" onClick={() => setInfo(selected || "image")} data-testid="guide-open-btn"><BookOpen className="w-4 h-4" /> دليل الأنواع والعينات</Button>
        </div>
        <TypeChooser catalog={wallet.catalog || []} credits={wallet.credits || {}} selected={selected} onPick={onPick} onInfo={setInfo} unlimited={!!wallet.unlimited} />
        {sel && (
          <p className="text-xs text-primary flex items-center gap-1" data-testid="hub-selected-hint">
            <CheckCircle2 className="w-3.5 h-3.5" /> الخدمة المختارة: <b>{sel.label}</b> — {!sel.price ? "مجانية؛ أكمل الحقول المظللة بالأسفل ثم اضغط توليد."
              : wallet.unlimited || (wallet.credits || {})[sel.kind] || (sel.fallback && (wallet.credits || {})[sel.fallback]) || (sel.group === "image" && (wallet.credits || {}).image) ? "لديك رصيد منها؛ أكمل الحقول المظللة بالأسفل، اعرض الشكل المتوقع، ثم اضغط توليد." : "لا رصيد منها بعد — اشترِها من «الباقات والأرصدة» (زر الشراء أعلى الشاشة) ثم ولّد."} <ArrowDown className="w-3.5 h-3.5 animate-bounce" />
          </p>
        )}
      </div>
      <GuideDialog guide={guide} kind={info} onClose={() => setInfo("")} onPick={onPick} />
      {!wallet.applied_coupon && <GiftSteps wallet={wallet} onActivated={load} />}
      {wallet.applied_coupon === "WELCOME" && (wallet.credits?.image > 0 || wallet.credits?.video > 0) && (
        <p className="text-xs rounded-xl border border-accent/40 bg-accent/5 px-3 py-2 flex items-center gap-2" data-testid="welcome-gift-active"><Gift className="w-4 h-4 text-accent shrink-0" /> هدية الترحيب مفعّلة في حسابك: صورتان + فيديو + {wallet.gift_wallet_sar} ريال رصيد — اختر خدمة من رصيدك بالأعلى وابدأ.</p>
      )}
      {pay !== null && <PaymentSoonDialog promoCode={promo?.code} isTrial={!!wallet.applied_coupon || user?.plan === "trial"} busy={busy} onActivate={activateFromPay} onClose={() => setPay(null)} />}
    </div>
  );
};
